import { useEffect, useMemo, useRef, useState } from "react"
import { Check, ChevronDown, Loader2, Search } from "lucide-react"
import type { AppShell } from "@/App"
import { Input } from "@/components/ui/input"
import { listProducts, effectiveCost, publicName, type ProductRow } from "@/lib/api"
import { formatEur } from "@/lib/format"
import { cn } from "@/lib/utils"

interface ProductComboboxProps {
  shell: AppShell | null
  role: string
  brandSlug: string | null
  value: number | null
  /**
   * Nome do produto já guardado na linha. Usado como fallback de exibição
   * quando o produto selecionado NÃO está nos candidatos carregados (filtrado
   * por marca/role, ou ainda a carregar) — evita mostrar "Selecionar produto…"
   * numa linha que já tem produto.
   */
  valueName?: string | null
  onChange: (product: ProductRow | null) => void
  disabled?: boolean
  /**
   * Módulo. Solar (default) filtra por category_roles + brand slug. AC/BC
   * filtram por categorias do módulo + additional_properties.<kind>.role.
   */
  kind?: "solar" | "ac" | "bc_aqs" | "bc_aq"
  /** AC/BC: marca da proposta — o picker só mostra equipamentos dessa marca. */
  moduleBrand?: string | null
}

/**
 * Search-as-you-type combobox para produtos. Carrega candidatos do role+brand
 * uma vez e filtra localmente por query. Mostra thumb (imagem), nome,
 * descrição truncada e preço base.
 */
export function ProductCombobox({
  shell,
  role,
  brandSlug,
  value,
  valueName,
  onChange,
  disabled,
  kind = "solar",
  moduleBrand,
}: ProductComboboxProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [items, setItems] = useState<ProductRow[]>([])
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Load candidates for this role+brand
  useEffect(() => {
    if (!shell || !role) return
    let cancelled = false
    setLoading(true)
    const query = kind === "solar"
      ? { role, brand_slug: brandSlug ?? undefined, limit: 100 }
      : { kind, metadata_role: role, brand: moduleBrand ?? undefined, limit: 100 }
    listProducts(shell, query)
      .then((rows) => {
        if (!cancelled) setItems(rows)
      })
      .catch(() => {
        if (!cancelled) setItems([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [shell, role, brandSlug, kind, moduleBrand])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [open])

  const selected = useMemo(
    () => items.find((p) => p.id === value) ?? null,
    [items, value],
  )

  const filtered = useMemo(() => {
    if (!query.trim()) return items.slice(0, 30)
    const q = query.toLowerCase()
    return items
      .filter(
        (p) =>
          publicName(p).toLowerCase().includes(q) ||
          p.name.toLowerCase().includes(q) ||
          (p.sku?.toLowerCase().includes(q) ?? false) ||
          (p.description?.toLowerCase().includes(q) ?? false),
      )
      .slice(0, 30)
  }, [items, query])

  function firstImage(p: ProductRow): string | null {
    if (Array.isArray(p.images) && p.images.length > 0) {
      const first = p.images[0]
      if (typeof first === "string") return first
      if (first && typeof first === "object" && "url" in first) {
        return (first as { url: string }).url
      }
    }
    return null
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={cn(
          "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-2 text-sm shadow-sm",
          "hover:bg-accent/5 focus:outline-none focus:ring-2 focus:ring-ring",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          {selected ? (
            <>
              {firstImage(selected) ? (
                <img
                  src={firstImage(selected)!}
                  alt=""
                  className="h-6 w-6 shrink-0 rounded object-cover"
                />
              ) : (
                <div className="h-6 w-6 shrink-0 rounded bg-muted" />
              )}
              <span className="truncate">{publicName(selected)}</span>
            </>
          ) : value != null && valueName ? (
            // Produto guardado na linha mas fora dos candidatos carregados
            // (filtro/timing) — mostra o nome guardado em vez do placeholder.
            <>
              <div className="h-6 w-6 shrink-0 rounded bg-muted" />
              <span className="truncate">{valueName}</span>
            </>
          ) : (
            <span className="text-muted-foreground">Selecionar produto…</span>
          )}
        </div>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-80 overflow-hidden rounded-md border border-border bg-popover shadow-lg">
          <div className="relative border-b border-border p-2">
            <Search className="absolute left-4 top-4 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Procurar..."
              className="h-8 pl-8 text-xs"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                A carregar…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-xs text-muted-foreground">
                {items.length === 0
                  ? `Sem produtos para role ${role}${
                      brandSlug ? ` + brand ${brandSlug}` : ""
                    }`
                  : "Sem resultados"}
              </div>
            ) : (
              filtered.map((p) => {
                const isSelected = p.id === value
                const img = firstImage(p)
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      onChange(p)
                      setOpen(false)
                      setQuery("")
                    }}
                    className={cn(
                      "flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-accent/10",
                      isSelected && "bg-accent/15",
                    )}
                  >
                    {img ? (
                      <img
                        src={img}
                        alt=""
                        className="h-8 w-8 shrink-0 rounded object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 shrink-0 rounded bg-muted" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{publicName(p)}</div>
                      {p.sku && (
                        <div className="truncate text-xs text-muted-foreground">
                          SKU: {p.sku}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-right text-xs">
                      <div className="font-semibold">
                        {formatEur(effectiveCost(p))}
                      </div>
                      {isSelected && (
                        <Check className="ml-auto mt-1 h-3 w-3 text-accent" />
                      )}
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
